/**
 * Catálogo de tipos de componente (`/api/tipos-componente`).
 *
 * Factor, característica y aspecto. La columna real es `nombre` pero el API la
 * expone como `tipo`; ambos se aceptan al escribir.
 *
 * Capa de servicios: una función por operación del API. No lanza nunca —cada
 * función devuelve un valor neutro ante el fallo—, así que el llamador debe
 * comprobar el resultado, no envolver en try/catch.
 */

import type TipoComponente from '@/interfaces/TipoComponente'
import { http } from '@/services/http'

/**
 * Consulta tipo componente · petición `GET`.
 *
 * No lanza: ante cualquier fallo (red, 4xx, 5xx) devuelve `[]`. Un array
 * vacío NO distingue «sin datos» de «la petición falló».
 *
 * @returns TipoComponente[]
 */
export async function getTiposComponenteService(url: string): Promise<TipoComponente[]> {
  const result = await http.get<any>(url)
  if (!result.ok) return []
  const d = result.data
  return Array.isArray(d) ? d : (d?.data ?? [])
}

/**
 * Consulta tipo componente · petición `GET`.
 *
 * No lanza: ante cualquier fallo (red, 4xx, 5xx) devuelve `null`. Un null
 * NO distingue «no encontrado» de «la petición falló».
 *
 * @returns TipoComponente | null
 */
export async function getTipoComponenteByIdService(url: string, id: number): Promise<TipoComponente | null> {
  const result = await http.get<TipoComponente>(`${url.replace(/\/$/, '')}/${id}`)
  return result.ok ? (result.data ?? null) : null
}

/**
 * Crea tipo componente · petición `POST`.
 *
 * No lanza: ante cualquier fallo (red, 4xx, 5xx) devuelve `null`. Un null
 * NO distingue «no encontrado» de «la petición falló».
 *
 * @returns TipoComponente | null
 */
export async function crearTipoComponenteService(url: string, payload: Partial<TipoComponente>): Promise<TipoComponente | null> {
  const result = await http.post<TipoComponente>(url, payload)
  return result.ok ? (result.data ?? null) : null
}

/**
 * Actualiza tipo componente · petición `PUT`.
 *
 * No lanza: ante cualquier fallo (red, 4xx, 5xx) devuelve `null`. Un null
 * NO distingue «no encontrado» de «la petición falló».
 *
 * @returns TipoComponente | null
 */
export async function updateTipoComponenteService(url: string, id: number, payload: Partial<TipoComponente>): Promise<TipoComponente | null> {
  const result = await http.put<TipoComponente>(`${url.replace(/\/$/, '')}/${id}`, payload)
  return result.ok ? (result.data ?? null) : null
}

/**
 * Elimina tipo componente · petición `DELETE`.
 *
 * No lanza: `true` solo si la petición tuvo éxito. `false` agrupa todos los
 * fallos posibles sin distinguirlos.
 *
 * @returns boolean
 */
export async function deleteTipoComponenteService(url: string, id: number): Promise<boolean> {
  const result = await http.delete(`${url.replace(/\/$/, '')}/${id}`)
  return result.ok
}
